import { useCallback, useEffect, useMemo, useState } from 'react';

import { DisputesOverview, PayoutsOverview, ReportsOverview } from '@adyen/adyen-platform-experience-web';
import { Box, CircularProgress, FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Redirect, Route, Switch } from 'react-router-dom';

import AdyenOverviewComponent from './AdyenOverviewComponent';
import AdyenTransactionOverviewComponent from './AdyenTransactionOverviewComponent';
import AdyenUnconnected from './AdyenUnconnected';
import { DesktopDrawerWidth } from '../../../components/AuthenticatedHeader/common';
import useApp from '../../../hooks/useApp';
import { getErrorMessage } from '../../../lib/error';
import Routes from '../../../lib/routes';
import { AdyenPaymentAccount, getSDKClient } from '../../../lib/sdk';

type AdyenRouterProps = {
  /**
   * This may not be present if the user has no customer apps
   */
  customerAppId?: string;
};

export default function AdyenRouter({ customerAppId }: AdyenRouterProps): JSX.Element {
  const { app } = useApp();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(true);
  const [adyenPaymentAccounts, setAdyenPaymentAccounts] = useState<AdyenPaymentAccount[]>([]);
  const [selectedPaymentAccount, setSelectedPaymentAccount] = useState<AdyenPaymentAccount | undefined>(undefined);
  const [isComponentInitialized, setIsComponentInitialized] = useState<boolean>(false);

  // Track loaded customer app to prevent stale UI data
  const [loadedCustomerAppId, setLoadedCustomerAppId] = useState<string | undefined>(undefined);

  const selectedPaymentAccountId = selectedPaymentAccount?.payment_account_id ?? '';
  const live = selectedPaymentAccount?.live ?? false;
  const isAdyenConnected = adyenPaymentAccounts.length > 0 && loadedCustomerAppId === customerAppId; // Prevent stale UI data
  const balanceAccountId = selectedPaymentAccount?.balance_account_id;

  const sdk = useMemo(() => {
    const { tokens, env } = app;
    const currentToken = tokens.get(env) ?? '';
    return getSDKClient(env, currentToken);
  }, [app]);

  useEffect(() => {
    // Reset state to avoid data staleness
    setAdyenPaymentAccounts([]);
    setSelectedPaymentAccount(undefined);
    setLoading(false);
    setLoadedCustomerAppId(undefined);
    setIsComponentInitialized(false);

    if (customerAppId === undefined) {
      return;
    }

    const checkIfAdyenIsConnected = async () => {
      setLoading(true);
      try {
        const paymentAccounts = await sdk.getAdyenPaymentAccounts(customerAppId);
        setAdyenPaymentAccounts(paymentAccounts.payment_accounts);

        if (paymentAccounts.payment_accounts.length > 0) {
          const firstPaymentAccount = paymentAccounts.payment_accounts[0];
          setSelectedPaymentAccount(firstPaymentAccount);
        }

        setLoadedCustomerAppId(customerAppId);
      } catch (err) {
        enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    checkIfAdyenIsConnected();
  }, [app, customerAppId, enqueueSnackbar, sdk]);

  const createAdyenSessionToken = useCallback(async () => {
    if (!customerAppId || !selectedPaymentAccountId) {
      return {
        id: '',
        token: '',
      };
    }

    try {
      const adyenSessionToken = await sdk.generateAdyenSessionToken(customerAppId, selectedPaymentAccountId);

      return {
        id: adyenSessionToken.adyen_session_token_id,
        token: adyenSessionToken.adyen_session_token,
      };
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    } finally {
      setIsComponentInitialized(true);
    }

    return {
      id: '',
      token: '',
    };
  }, [customerAppId, enqueueSnackbar, selectedPaymentAccountId, sdk]);

  if (loading) {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: { lg: `calc(50% + ${DesktopDrawerWidth} / 2)`, xs: '50%' },
          transform: 'translate(-50%, -50%)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack gap={2}>
      <FormControl>
        <InputLabel>Adyen Accountholder ID</InputLabel>
        <Select
          value={selectedPaymentAccountId}
          onChange={(e) =>
            setSelectedPaymentAccount(
              adyenPaymentAccounts.find((paymentAccount) => paymentAccount.payment_account_id === e.target.value),
            )
          }
          label="Adyen Accountholder ID"
          disabled={!isAdyenConnected}
        >
          {adyenPaymentAccounts.map((paymentAccount) => (
            <MenuItem key={paymentAccount.payment_account_id} value={paymentAccount.payment_account_id}>
              {paymentAccount.legal_entity_name} ({paymentAccount.adyen_accountholder_id})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {!isAdyenConnected ? (
        <AdyenUnconnected />
      ) : (
        <>
          <Switch>
            <Route path={Routes.Integrations.Adyen.Transactions}>
              <AdyenTransactionOverviewComponent
                setIsComponentInitialized={setIsComponentInitialized}
                isComponentInitialized={isComponentInitialized}
                customerAppId={customerAppId}
                getAdyenSessionToken={createAdyenSessionToken}
                live={live}
                balanceAccountId={balanceAccountId}
              />
            </Route>
            <Route path={Routes.Integrations.Adyen.Payouts}>
              <AdyenOverviewComponent
                isComponentInitialized={isComponentInitialized}
                setIsComponentInitialized={setIsComponentInitialized}
                overviewClass={PayoutsOverview}
                containerId="payouts-overview-container"
                getAdyenSessionToken={createAdyenSessionToken}
                live={live}
                balanceAccountId={balanceAccountId}
              />
            </Route>
            <Route path={Routes.Integrations.Adyen.Reports}>
              <AdyenOverviewComponent
                isComponentInitialized={isComponentInitialized}
                setIsComponentInitialized={setIsComponentInitialized}
                overviewClass={ReportsOverview}
                containerId="reports-overview-container"
                getAdyenSessionToken={createAdyenSessionToken}
                live={live}
                balanceAccountId={balanceAccountId}
              />
            </Route>
            <Route path={Routes.Integrations.Adyen.Disputes}>
              <AdyenOverviewComponent
                isComponentInitialized={isComponentInitialized}
                setIsComponentInitialized={setIsComponentInitialized}
                overviewClass={DisputesOverview}
                containerId="disputes-overview-container"
                getAdyenSessionToken={createAdyenSessionToken}
                live={live}
                balanceAccountId={balanceAccountId}
              />
            </Route>
            <Route path={Routes.Integrations.Adyen.Base}>
              <Redirect to={Routes.Integrations.Adyen.Transactions} />
            </Route>
          </Switch>
        </>
      )}
    </Stack>
  );
}
